import { portalApi } from "./client";

// Annual Vendor Audit — the vendor's side. Mirrors the C# records the backend
// exposes under /api/portal/vendor/audits. Kept inside the portal bundle so
// nothing here imports staff types. No React Query in the portal — callers load
// with useState and re-run the loader after a reply.

/** The statuses a vendor can ever see. `draft` and `superseded` are filtered out
 *  server-side and deliberately absent from this union: a draft is an unfinished
 *  internal document and a superseded one is a position Phoenix has retired. */
export type VendorAuditStatus =
  | "in-vendor-review"
  | "awaiting-final-assessment"
  | "completed";

export type VendorAuditDetermination = "competitive" | "not-competitive" | "inconclusive";
export type VendorAuditRecommendation = "retain" | "renegotiate" | "replace";

/** Which side of the discussion an entry came from. Same strings as the staff
 *  surface, so one conversation cannot be attributed two different ways. */
export type VendorAuditCommentRole = "phoenix" | "vendor";

export interface VendorAuditSummary {
  id: string;
  auditYear: number;
  status: VendorAuditStatus;
  /** Whether the discussion accepts a reply right now. Server-derived — the
   *  window rule belongs to the backend, not to a branch in this bundle. */
  isOpenForComment: boolean;
  releasedAt: string | null;
  reviewClosesAt: string | null;
  reviewClosedAt: string | null;
  /** Whole days left in the window, floored at 0; null when it is not open. */
  daysRemaining: number | null;
  finalDetermination: VendorAuditDetermination | null;
  finalRecommendation: VendorAuditRecommendation | null;
  completedAt: string | null;
  commentCount: number;
}

/** One competitor Phoenix approached. `declined` is a RESULT, not a blank row —
 *  render it as "would not quote", never as an empty line. */
export interface VendorAuditQuote {
  id: string;
  competitorName: string;
  contactName: string | null;
  contactMethod: string | null;
  /** A wall-calendar date (the day they were contacted), not an instant —
   *  format it with formatCalendarDate, never the tz-aware renderer. */
  contactedOn: string | null;
  quotedTerms: string | null;
  servicesOffered: string | null;
  declined: boolean;
  sortOrder: number;
}

export interface VendorAuditComment {
  id: string;
  authorRole: VendorAuditCommentRole;
  /** Always populated. A Phoenix entry is always the firm name — the audit is
   *  the company's position, not a named individual's opinion of it. */
  authorName: string;
  body: string;
  createdAt: string;
}

export interface VendorAuditDetail {
  id: string;
  auditYear: number;
  status: VendorAuditStatus;
  isOpenForComment: boolean;
  releasedAt: string | null;
  reviewClosesAt: string | null;
  reviewClosedAt: string | null;
  daysRemaining: number | null;
  phoenixPricingSummary: string | null;
  phoenixServicesSummary: string | null;
  preliminaryFinding: string | null;
  finalDetermination: VendorAuditDetermination | null;
  finalRecommendation: VendorAuditRecommendation | null;
  finalNarrative: string | null;
  completedAt: string | null;
  quotes: VendorAuditQuote[];
  comments: VendorAuditComment[];
}

export const vendorAuditApi = {
  /** Every market check released to this vendor, newest year first. */
  list: () => portalApi.get<VendorAuditSummary[]>("/vendor/audits"),
  get: (auditId: string) => portalApi.get<VendorAuditDetail>(`/vendor/audits/${auditId}`),
  /** Append the vendor's reply. Refused (400) once the window has closed. */
  comment: (auditId: string, body: string) =>
    portalApi.post<VendorAuditDetail>(`/vendor/audits/${auditId}/comments`, { body }),
};
